import React from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@swyftx/aviary/atoms/Card';
import { Notification } from '@swyftx/aviary/atoms/Notification';
import { Skeleton } from '@swyftx/aviary/atoms/Skeleton';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { Bank, Bitcoin, Copy, Cross, Tick, Dollar, Delete, Clock24Hours } from '@swyftx/aviary/icons/outlined';

import { LoanApplication } from '@shared/api/@types/loans';
import { cn } from '@shared/utils/lib/ui';

import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

export const ApplicationStatusCard = ({
  application,
  isFetchingApplications,
}: {
  application?: LoanApplication;
  isFetchingApplications: boolean;
}) => {
  const { navigate } = useNavigateRoute();

  if (!application) {
    return isFetchingApplications ? <Skeleton className='mb-8 h-[320px] w-full' /> : null;
  }

  const { status, securityAssetCode } = application;

  const statusOrder = [
    'DRAFT',
    'PENDING_DEPOSIT',
    'SUBMITTED_TO_BLOCKEARNER',
    'APPROVED',
    'SECURITY_PROVIDED',
    'FUNDS_RECEIVED',
  ];

  const currentStatusIndex = status ? statusOrder.indexOf(status) : -1;

  const steps = [
    {
      key: 'SUBMIT',
      title: 'Submit application',
      description: 'Customize your loan terms and submit your application.',
      icon: Copy,
      statuses: ['DRAFT'],
      completedIndex: 0,
    },
    {
      key: 'DEPOSIT',
      title: 'Nominate bank',
      description: "Deposit $1 to verify where you'd like to receive your loan funds.",
      icon: Bank,
      statuses: ['PENDING_DEPOSIT'],
      completedIndex: 1,
    },
    {
      key: 'APPROVAL',
      title: 'Wait for approval',
      description: 'Your application is being reviewed. This may take up to 24 business hours.',
      icon: Clock24Hours,
      statuses: ['SUBMITTED_TO_BLOCKEARNER'],
      completedIndex: 2,
    },
    {
      key: 'SECURITY',
      title: `Transfer ${securityAssetCode}`,
      description: `You've been approved! Transfer ${securityAssetCode} to finalise your loan.`,
      icon: Bitcoin,
      statuses: ['APPROVED'],
      completedIndex: 3,
    },
    {
      key: 'FUNDS',
      title: 'Receive your funds',
      description: "We're processing your funds. This may take up to 24 business hours.",
      icon: Dollar,
      statuses: [],
      completedIndex: 4,
    },
  ] as const;

  const getStepState = (step: {
    key: 'SUBMIT' | 'DEPOSIT' | 'APPROVAL' | 'SECURITY' | 'FUNDS';
    title?: string;
    description?: string;
    icon?: React.FC<React.ImgHTMLAttributes<SVGElement>>;
    statuses: readonly string[];
    completedIndex: number;
  }) => {
    if (status === 'DECLINED' && step.key === 'APPROVAL') return 'declined';
    if (status === 'CANCELLED') return 'cancelled';
    if (currentStatusIndex >= step.completedIndex) {
      if (step.statuses.includes(status)) return 'active';
      return 'completed';
    }
    return 'pending';
  };

  const ApplicationDeclined = () => (
    <Notification title='Loan application declined' severity='destructive' className='mb-16'>
      <Body>Unfortunately, your loan application has been declined at this time</Body>
      <div className='mt-12 flex gap-2'>
        <Button size='sm' onClick={() => navigate(NavigationURLs.LoansApplication)}>
          Start new application
        </Button>
      </div>
    </Notification>
  );

  return (
    <>
      {status === 'DECLINED' && !isFetchingApplications && <ApplicationDeclined />}

      <Card className='mb-8'>
        <CardHeader className='border-b'>
          <CardTitle className='flex items-center gap-2 text-lg'>
            <Copy className='h-24 w-24' />
            Application status
          </CardTitle>
        </CardHeader>
        <CardContent className='pt-24'>
          <div className='relative grid grid-cols-1 gap-y-16 md:grid-cols-5 md:gap-x-8 md:gap-y-0'>
            {steps.map((step, index) => {
              const state = getStepState(step);
              const isDeclinedStep = state === 'declined';
              const isPrevStepCompleted = index > 0 && getStepState(steps[index - 1]) === 'completed';

              return (
                <div key={step.key} className='relative flex items-start gap-16 md:gap-4'>
                  <div
                    aria-hidden='true'
                    className={cn(
                      'absolute left-20 top-44 h-[calc(100%-32px)] w-px border-l-2 md:hidden',
                      state === 'completed'
                        ? 'border-solid border-color-background-primary'
                        : state === 'active'
                        ? 'border-dashed'
                        : 'border-color-border-default border-dotted',
                    )}
                  />

                  <div className='flex items-center'>
                    {index > 0 && (
                      <div
                        aria-hidden='true'
                        className={cn(
                          'mr-8 hidden h-px w-24 border-t-2 lg:block',
                          isPrevStepCompleted ? 'border-solid' : 'border-dotted',
                          isPrevStepCompleted ? 'border-color-background-primary' : 'border-color-border-default',
                        )}
                      />
                    )}

                    <div
                      className={cn(
                        'z-10 flex h-40 w-40 flex-shrink-0 items-center justify-center rounded-full transition-colors',
                        {
                          'bg-color-background-primary-subtle text-color-text-primary': state === 'completed',
                          'bg-color-background-primary text-color-text-inverse': state === 'active',
                          'bg-color-background-neutral text-color-text-neutral': state === 'pending',
                          'bg-color-background-error text-color-icon-inverse': isDeclinedStep,
                        },
                      )}
                    >
                      {state === 'completed' ? (
                        <Tick className='h-16 w-16' />
                      ) : isDeclinedStep ? (
                        <Cross className='h-16 w-16' />
                      ) : (
                        <step.icon className='h-24 w-24' />
                      )}
                    </div>
                  </div>

                  <div className='flex-1 pl-0 md:pl-4'>
                    <div className='space-y-4'>
                      <Skeleton loading={isFetchingApplications} className='w-3/4'>
                        <Body className='text-sm font-semibold'>
                          {isDeclinedStep ? 'Decision reached: Unsuccessful' : step.title}
                        </Body>
                      </Skeleton>
                      <Skeleton loading={isFetchingApplications} className='w-full'>
                        <Body className='text-color-text- text-xs'>
                          {isDeclinedStep
                            ? 'Your application has been reviewed, and a decision has been made.'
                            : step.description}
                        </Body>
                      </Skeleton>
                    </div>

                    {!isFetchingApplications && (
                      <div className='mt-8'>
                        <div className='flex flex-col gap-8'>
                          {status === 'DRAFT' && step.key === 'SUBMIT' && (
                            <>
                              <Button onClick={() => navigate(NavigationURLs.LoansApplication)}>
                                Resume application
                              </Button>
                              <Button variant='outlined' leadingIcon={<Delete className='h-16 w-16' />}>
                                Cancel application
                              </Button>
                            </>
                          )}
                          {status === 'PENDING_DEPOSIT' && step.key === 'DEPOSIT' && (
                            <Button variant='outlined'>View deposit info</Button>
                          )}
                          {status === 'APPROVED' && step.key === 'SECURITY' && <Button>Provide security</Button>}
                          {isDeclinedStep && (
                            <div className='flex flex-col items-start gap-8'>
                              <Button onClick={() => navigate(NavigationURLs.LoansApplication)}>
                                Start new application
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
};
